/**
 * Performance tracking complete callback data
 */
export interface IPerformanceCompleteData {
	name: string;
	operation: string;
	duration: number;
	attributes?: Record<string, unknown>;
}

/**
 * Performance tracking error callback data
 */
export interface IPerformanceErrorData {
	name: string;
	operation: string;
	duration: number;
	attributes?: Record<string, unknown>;
	error: unknown;
}

export type TOnPerformanceComplete = (data: IPerformanceCompleteData) => void;
export type TOnPerformanceError = (data: IPerformanceErrorData) => void;

/**
 * Track performance of async operations without Sentry
 * Fallback implementation when Sentry is not available
 * @param name - Operation name
 * @param operation - Operation type
 * @param callback - Async callback to execute
 * @param attributes - Optional attributes
 * @param onComplete - Callback invoked on successful completion
 * @param onError - Callback invoked on error
 * @returns Result of callback
 */
export async function trackPerformanceFallback<T>(
	name: string,
	operation: string,
	callback: () => Promise<T>,
	attributes: Record<string, unknown> | undefined,
	onComplete: TOnPerformanceComplete,
	onError: TOnPerformanceError
): Promise<T> {
	const startTime = performance.now();
	try {
		const result = await callback();
		const duration = performance.now() - startTime;
		onComplete({
			name,
			operation,
			duration,
			attributes,
		});
		return result;
	} catch (error) {
		const duration = performance.now() - startTime;
		onError({
			name,
			operation,
			duration,
			attributes,
			error,
		});
		throw error;
	}
}
